import { Guess } from '@wt/app/game/_components/types';
import { getSiteMode } from '@wt/utilities/siteMode';

export function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function getDistanceText(
  distance: number,
  unit?: string,
  abbreviated?: boolean
) {
  let numericPart: string;
  let suffix: string;

  if (unit === 'miles') {
    if (distance < 1609.34) {
      distance *= 3.28084;
      suffix = 'ft';

      if (abbreviated) {
        if (distance >= 1000) {
          numericPart = (distance / 1000).toFixed(1) + 'K';
        } else if (distance >= 100) {
          numericPart = Math.round(distance).toString();
        } else {
          numericPart = distance.toFixed(1);
        }
      } else {
        numericPart = distance.toFixed(1);
      }
    } else {
      distance /= 1609.34;
      suffix = 'mi';

      if (abbreviated) {
        if (distance >= 1000) {
          numericPart = (distance / 1000).toFixed(1) + 'K';
        } else if (distance >= 100) {
          numericPart = Math.round(distance).toString();
        } else {
          numericPart = distance.toFixed(1);
        }
      } else {
        numericPart = Math.round(Number(distance.toFixed(0))).toString();
      }
    }
  } else {
    if (distance < 1000) {
      suffix = 'm';

      if (abbreviated) {
        if (distance >= 100) {
          numericPart = Math.round(distance).toString();
        } else {
          numericPart = distance.toFixed(1);
        }
      } else {
        numericPart = distance.toFixed(1);
      }
    } else {
      distance /= 1000;
      suffix = 'km';

      if (abbreviated) {
        if (distance >= 1000) {
          numericPart = (distance / 1000).toFixed(1) + 'K';
        } else if (distance >= 100) {
          numericPart = Math.round(distance).toString();
        } else {
          numericPart = distance.toFixed(1);
        }
      } else {
        numericPart = Math.round(Number(distance.toFixed(0))).toString();
      }
    }
  }

  return `${numericPart} ${suffix}`;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

export const MAX_DISTANCE_SCORE = 100;
export const MAX_YEAR_SCORE = 100;

export function getMaxScore() {
  if (getSiteMode() != 'wheretaken') {
    return MAX_YEAR_SCORE + MAX_DISTANCE_SCORE;
  } else {
    return MAX_DISTANCE_SCORE * 2;
  }
}

const SAME_COUNTRY_POINTS = 10;

export interface Score {
  total: number;
  year: number;
  distance: number;
}

export interface ScoreResultProps {
  distanceText: string;
  distanceTextAbbr: string;
  yearsText: string;
  distance: number;
  yearsOff: number;
  score: Score;
}

const getYearScore = (yearsOff: number) => {
  let yearScore: number;

  if (yearsOff <= 5) {
    yearScore = MAX_YEAR_SCORE - yearsOff;
  } else if (yearsOff <= 10) {
    yearScore = MAX_YEAR_SCORE - 5 - (yearsOff - 5) * 2;
  } else if (yearsOff <= 20) {
    yearScore = MAX_YEAR_SCORE - 15 - (yearsOff - 10) * 3;
  } else {
    yearScore = MAX_YEAR_SCORE - 45 - (yearsOff - 20) * 4;
  }

  return yearScore < 0 ? 0 : Math.round(yearScore);
};

const getDistanceScore = (dist: number, sameCountry: boolean) => {
  const d = Math.round(dist / 1000); // distance off in km
  let distanceScore: number;
  const sameCountryBonus = sameCountry ? SAME_COUNTRY_POINTS : 0;

  if (d <= 100) {
    distanceScore = MAX_DISTANCE_SCORE - d * 0.05;
  } else if (d <= 500) {
    distanceScore = 95 - (d - 101) * 0.025 + sameCountryBonus;
  } else if (d <= 1000) {
    distanceScore = 85 - (d - 501) * 0.03 + sameCountryBonus;
  } else if (d <= 2000) {
    distanceScore = 70 - (d - 1001) * 0.02 + sameCountryBonus;
  } else if (d <= 5000) {
    distanceScore = 50 - (d - 2001) * 0.01 + sameCountryBonus;
  } else if (d <= 10000) {
    distanceScore = 20 - (d - 5001) * 0.004 + sameCountryBonus;
  } else {
    distanceScore = sameCountryBonus;
  }

  return distanceScore < 0 ? 0 : Math.round(distanceScore);
};

const guessScores: Record<string, ScoreResultProps> = {};

export function getGameScore(guess: Guess, unit?: string): ScoreResultProps {
  const key =
    getSiteMode() != 'wheretaken'
      ? `${guess.year} ${guess.location}${guess.locationSecondary ?? ''} ${guess.puzzle.puzzle} ${unit ?? 'km'}`
      : `${guess.location}${guess.locationSecondary ?? ''} ${guess.puzzle.puzzle} ${unit ?? 'km'}`;
  if (key in guessScores) {
    return guessScores[key];
  }

  const yearBuffer = guess.puzzle.yearBuffer ?? 0;
  let yearsOff = 0;
  if (getSiteMode() != 'wheretaken') {
    if (
      guess.year == guess.puzzle.year - yearBuffer ||
      guess.year == guess.puzzle.year + yearBuffer
    ) {
      yearsOff = 0;
    } else if (guess.year < guess.puzzle.year - yearBuffer) {
      yearsOff = guess.puzzle.year - yearBuffer - guess.year;
    } else if (guess.year > guess.puzzle.year + yearBuffer) {
      yearsOff = guess.year - (guess.puzzle.year + yearBuffer);
    } else {
      yearsOff = 0;
    }
  }

  const distance1 =
    1000 *
    getDistanceFromLatLonInKm(
      Number(guess.location.latitude),
      Number(guess.location.longitude),
      guess.puzzle.latitude,
      guess.puzzle.longitude
    );
  const distance2 = guess.locationSecondary
    ? 1000 *
      getDistanceFromLatLonInKm(
        Number(guess.locationSecondary.latitude),
        Number(guess.locationSecondary.longitude),
        guess.puzzle.latitude,
        guess.puzzle.longitude
      )
    : Number.MAX_VALUE;
  const distance = Math.min(distance1, distance2);
  const distanceText = getDistanceText(distance, unit);
  const distanceTextAbbr = getDistanceText(distance, unit, true);

  let yearsText: string;
  if (yearsOff == 0) {
    yearsText = 'You got it spot on!';
  } else if (yearsOff == 1) {
    yearsText = 'You were 1 year off';
  } else {
    yearsText = 'You were ' + yearsOff + ' years off';
  }

  const yearScore = getSiteMode() != 'wheretaken' ? getYearScore(yearsOff) : 0;
  const factor = getSiteMode() != 'wheretaken' ? 1 : 2;
  const distanceScore =
    getDistanceScore(distance, guess.correctCountry ?? false) * factor;
  const totalScore = yearScore + distanceScore;

  const result: ScoreResultProps = {
    distanceText,
    distanceTextAbbr,
    yearsText,
    distance,
    yearsOff,
    score: {
      total: Math.round(totalScore),
      year: Math.round(yearScore),
      distance: Math.round(distanceScore),
    },
  };
  guessScores[key] = result;
  return result;
}

const botScores: Record<string, Score> = {};

export function getBotScore(guess: Guess): Score {
  const key =
    getSiteMode() != 'wheretaken'
      ? `${guess.year} ${guess.location} ${guess.puzzle.puzzle}${guess.puzzle.year}${guess.puzzle.latitude}${guess.puzzle.longitude}`
      : `${guess.location} ${guess.puzzle.puzzle}${guess.puzzle.latitude}${guess.puzzle.longitude}`;
  if (key in botScores) {
    return botScores[key];
  }

  const yearBuffer = guess.puzzle.yearBuffer ?? 0;
  const yearsOffLeft = Math.abs(guess.year - (guess.puzzle.year - yearBuffer));
  const yearsOffRight = Math.abs(guess.year - (guess.puzzle.year + yearBuffer));

  const yearsOff =
    getSiteMode() != 'wheretaken' ? Math.min(yearsOffLeft, yearsOffRight) : 0;
  const distance =
    1000 *
    getDistanceFromLatLonInKm(
      Number(guess.location.latitude),
      Number(guess.location.longitude),
      guess.puzzle.latitude,
      guess.puzzle.longitude
    );

  const yearScore = getSiteMode() != 'wheretaken' ? getYearScore(yearsOff) : 0;
  const factor = getSiteMode() != 'wheretaken' ? 1 : 2;
  const distanceScore =
    getDistanceScore(distance, guess.correctCountry ?? false) * factor;
  const totalScore = yearScore + distanceScore;

  const result: Score = {
    total: Math.round(totalScore),
    year: Math.round(yearScore),
    distance: Math.round(distanceScore),
  };

  botScores[key] = result;
  return result;
}
